// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { itemDetailSchema } from '@/items/api/types';
import { ItemType } from '@/items/constants/item-types';
import { z } from 'zod';

/**
 * Parameters for updating an item.
 * All fields are optional; only provided fields will be updated.
 */
export interface UpdateItemParameters {
  name?: string;
  sku?: string | null;
  description?: string | null;
  isArchived?: boolean;
  type?: ItemType;
  acceptsBulkSerials?: boolean;
  fields?: {
    fieldId: string;
    order: number;
    section: number;
  }[];
}

/**
 * Schema for the update item API response.
 */
const updateItemResponseSchema = z
  .object({ data: itemDetailSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, item: data }));

type UpdateItemResponse = z.infer<typeof updateItemResponseSchema>;

/**
 * Updates an item's details.
 * Returns the updated item including fields, accounting, and BOM data.
 */
export async function updateItem(
  key: string,
  parameters: UpdateItemParameters
): Promise<UpdateItemResponse> {
  const url = `/v1/catalog/item/${key}`;
  const response = await api.patch(url, parameters);
  return parseResponse(updateItemResponseSchema, response.data, {
    schemaName: 'UpdateItem',
    endpoint: url,
  });
}
